import Image from 'next/future/image';
import Link from 'next/link';
import { PropsWithChildren, useState } from 'react';
import { ReebeloStoreT } from '@lambda/reebelo';
import { useRouter } from 'next/router';
import cn from 'classnames';
import fbIcon from '@/public/icons/marketing/facebook.svg';
import igIcon from '@/public/icons/marketing/instagram.svg';
import youtubeIcon from '@/public/icons/marketing/youtube.svg';
import tiktokIcon from '@/public/icons/marketing/tiktok.svg';
import imgAU from '@/public/icons/country-flags/AU.svg';
import imgNZ from '@/public/icons/country-flags/NZ.svg';
import imgSG from '@/public/icons/country-flags/SG.svg';
import imgUS from '@/public/icons/country-flags/US.svg';
import imgCA from '@/public/icons/country-flags/CA.svg';
import arrowBottom from '@/public/icons/menu/arrow-bottom.svg';
import letterIcon from '@/public/icons/menu/letter.svg';
import linkedinIcon from '@/public/icons/marketing/linkedin.svg';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import SubscribeNewsletter from './SubscribeNewsletter';
import LangCountrySelecter from './LangCountrySelecter';
import Accordion from '../Accordion';
import { useMediaQuery } from '../hooks';
import { Breakpoints } from '@/settings/breakpoints';
import PaymentIcons from '@/components/collections/[handle]/NewProductPage/PaymentIcons';

const t = i18n.init();
const FLAGS: Record<
  Exclude<ReebeloStoreT, 'reebelo-kr' | 'reebelo-tw'>,
  string
> = {
  'reebelo-au': imgAU,
  'reebelo-us': imgUS,
  'reebelo-nz': imgNZ,
  quista: imgSG,
  'reebelo-dev': imgSG,
  'reebelo-ca': imgCA,
};

const FooterLink = ({
  title,
  link,
  target,
}: {
  title: string;
  link: string;
  target?: string;
}) => (
  <li>
    <Link href={link}>
      <a className="hover:text-teal-500" target={target || '_self'}>
        {title}
      </a>
    </Link>
  </li>
);
const FooterHeading = ({
  title,
  isOpen,
}: {
  title: string;
  isOpen?: boolean;
}) => (
  <h4 className="relative w-full py-4 text-left font-extrabold sm:z-10 sm:p-0">
    {title}
    <div
      className={cn(
        'collapse-icon absolute top-1/2 right-0 z-0 block h-[16px] w-[16px] -translate-y-1/2 transition-all duration-300 ease-in-out sm:hidden',
        {
          'rotate-180': isOpen,
          'rotate-0': !isOpen,
        },
      )}
    >
      <Image
        src={arrowBottom}
        alt="collapse"
        sizes="100vw"
        className="h-full w-full object-contain"
      />
    </div>
  </h4>
);

const AccordionWrapper = (props: PropsWithChildren<{ title: string }>) => {
  const accordionContainerCss = 'relative flex flex-col';
  const isMobile = useMediaQuery(Breakpoints.SM);
  const { children, title } = props;

  return (
    <Accordion
      defaultOpen={!isMobile}
      titleComponent={<FooterHeading title={title} />}
      buttonClassName={`${!isMobile ? 'pointer-events-none' : ''}`}
      as="div"
      className={accordionContainerCss}
      key={`${title}-${isMobile}`}
    >
      {children}
    </Accordion>
  );
};

export default function Footer() {
  const [openCountryPopup, setCountryPopup] = useState(false);

  const router = useRouter();
  const ulCss = '-mt-1 mb-2 sm:mb-0 sm:mt-2 flex flex-col gap-1 xs:gap-2';

  const copyrightText = `© Reebelo ${settings.country} | ${settings.company_no}`;

  return (
    <div
      className={cn(
        'bg-gray-700 pt-6 pb-8 text-sm text-white xs:pt-8 sm:pb-0 sm:pt-14 lg:pt-16',
        {
          'mb-16 md:mb-0':
            router.pathname === '/account/orders' ||
            router.pathname === '/account/addresses',
        },
      )}
    >
      <div className="reebelo-container">
        <div className="accordion mb-12 flex flex-col sm:grid sm:grid-cols-3 sm:gap-6 lg:grid-cols-4">
          <AccordionWrapper title="ABOUT">
            <ul className={ulCss} id={`e2e-about-footer`}>
              <FooterLink title={t`About Reebelo`} link="/about-us" />
              <FooterLink
                title={t`Our Blog`}
                link={`${settings.externals.blog}`}
              />
              <FooterLink title={t`Customer Reviews`} link="/reviews" />
              <FooterLink
                title={t`We're Hiring!`}
                link="https://jobs.lever.co/reebelo"
              />
            </ul>
          </AccordionWrapper>
          <AccordionWrapper title="TERMS">
            <ul className={ulCss} id={`e2e-terms-footer`}>
              <FooterLink
                title={t`Warranty & Refunds`}
                link="/policies/warranty-and-refund-policy"
              />
              <FooterLink
                title={t`Shipping Policy`}
                link="/policies/shipping-policy"
              />
              <FooterLink
                title={t`Terms Of Service`}
                link="/policies/terms-of-service"
              />
              <FooterLink
                title={t`Privacy Policy`}
                link="/policies/privacy-policy"
              />
              {settings.store === 'reebelo-us' && (
                <FooterLink title="DMCA Policy" link="/policies/dmca-policy" />
              )}
              {settings.store === 'reebelo-ca' && (
                <FooterLink
                  title="Intellectual Property Policy"
                  link="/policies/dmca-policy"
                />
              )}
            </ul>
          </AccordionWrapper>
          <AccordionWrapper title="HELP">
            <ul className={ulCss} id={`e2e-help-footer`}>
              <FooterLink
                title="Track My Order"
                link={settings.externals.help_track_order}
              />
              {settings.store === 'reebelo-ca' ? (
                <FooterLink
                  title="Help Center"
                  link={settings.externals.help_center}
                />
              ) : (
                <FooterLink title="Help Center" link="/help" />
              )}
              <FooterLink
                title="Returns & Claims"
                link={settings.externals.help_return_claim}
              />
              <li>
                <a
                  className="my-1 mr-4 flex h-9 max-w-[50%] items-center justify-between rounded-sm bg-white px-3 text-[10px] font-thin text-gray-700 xxs:max-w-[135px] "
                  href="/help"
                >
                  <span className="-mb-1 font-extrabold uppercase !leading-none tracking-wide text-gray-700">{t`Contact Us`}</span>
                  <div className="ml-2 w-[18px]">
                    <Image
                      src={letterIcon}
                      alt="contact us button"
                      width="16"
                      height="12"
                      sizes="100vw"
                    />
                  </div>
                </a>
              </li>
              <FooterLink
                title="Sell on Reebelo"
                link={settings.externals.vendor_sign_up}
                target="_blank"
              />
            </ul>
          </AccordionWrapper>
          <div className="col-span-2 mt-10 xxs:mt-2 xs:mt-3 sm:mt-0 lg:col-span-1">
            <SubscribeNewsletter />
          </div>
        </div>
        <LangCountrySelecter
          isOpen={openCountryPopup}
          onClose={() => setCountryPopup(false)}
        />
        <hr className="hidden border-white/20 sm:block" />
        <div className="mt-4 mb-4 flex flex-col items-center justify-between gap-3 pb-2 xxs:mt-6 xs:mt-8 sm:mb-8 sm:flex-row sm:gap-0">
          <div
            onClick={() => setCountryPopup(true)}
            className="relative flex w-full cursor-pointer items-center rounded bg-white/10 py-2 px-3 sm:w-auto"
          >
            <Image
              src={FLAGS[settings.store]}
              alt="Australia"
              width={28}
              height={20}
              className="object-contain"
            />
            <span className="ml-3 min-w-[130px] border-l-2 border-gray-700 pl-4 text-xs leading-6">
              English
            </span>
            <span className="absolute top-2/4 right-3 inline-flex translate-y-[-50%] items-center justify-center">
              <div className="w-[14px]">
                <Image
                  src={arrowBottom}
                  alt="collapse"
                  width={14}
                  height={14}
                  sizes="100vw"
                  className="object-contain"
                />
              </div>
            </span>
          </div>
          <div className="mt-5 flex justify-center gap-6 sm:mt-0 sm:gap-8 md:gap-10 lg:gap-12">
            <a
              className="hover:text-teal-500"
              href={settings.marketing.tiktok_url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="inline-block h-[17px] w-[17px]">
                <Image
                  src={tiktokIcon}
                  alt="Tiktok"
                  width={17}
                  height={17}
                  sizes="100vw"
                  className="object-contain"
                />
              </span>
            </a>
            <a
              className="hover:text-teal-500"
              href={settings.marketing.youtube_url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="inline-block h-[20px] w-[22px]">
                <Image
                  src={youtubeIcon}
                  alt="YouTube"
                  width={22}
                  height={20}
                  sizes="100vw"
                  className="h-full w-full object-contain"
                />
              </span>
            </a>
            <a
              className="hover:text-teal-500"
              href={settings.marketing.facebook_url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="inline-block h-[17px] w-[17px]">
                <Image
                  src={fbIcon}
                  alt="facebook"
                  width={17}
                  height={17}
                  sizes="100vw"
                  className="object-contain"
                />
              </span>
            </a>
            <a
              className="hover:text-teal-500"
              href={settings.marketing.instagram_url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="inline-block h-[17px] w-[17px]">
                <Image
                  src={igIcon}
                  alt="intagram"
                  width={17}
                  height={17}
                  sizes="100vw"
                  className="object-contain"
                />
              </span>
            </a>
            <a
              className="hover:text-teal-500"
              href={settings.marketing.linkedin_url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="inline-block h-[17px] w-[17px]">
                <Image
                  src={linkedinIcon}
                  alt="linkedin"
                  width={17}
                  height={17}
                  sizes="100vw"
                  className="object-contain"
                />
              </span>
            </a>
          </div>
        </div>
      </div>
      <div className="bg-white py-4">
        <div className="reebelo-container flex flex-col-reverse justify-between lg:flex-row lg:gap-10">
          <div
            className={cn(
              'mt-4 flex flex-col flex-wrap items-start justify-start text-gray-700 sm:mb-0 lg:mt-0 lg:justify-end',
              {
                'mb-11':
                  router.pathname === '/collections/[handle]' ||
                  router.pathname === '/products/[handle]',
              },
            )}
          >
            <p className="w-full text-center text-xs sm:text-sm lg:text-left">
              {copyrightText}
            </p>
            {settings.country_code === 'SG' && (
              <p className="w-full text-center text-[10px] !leading-tight sm:text-[12px] lg:text-left">
                QUISTA TECHNOLOGY PTE. LTD. is not regulated and does not
                purport to be regulated or to hold any form of licence issued by
                the Monetary Authority of Singapore.
              </p>
            )}
          </div>
          <div className="flex min-w-[200px] flex-col items-center">
            <div className="mb-2 text-xs text-gray-700">{t`100% Secure Payments`}</div>
            <div className="flex justify-center gap-2 xxs:gap-3">
              <PaymentIcons hideTitle />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
